import {
  Text,
  Stack,
  Box,
  Group,
  UnstyledButton,
  TextInput,
  ActionIcon,
  Switch,
  NumberInput,
  Select,
  Divider,
} from '@mantine/core';
import {
  ONE_ON_ONE_TYPES,
  ONE_ON_ONE_TYPE_LABELS,
  DEFAULT_ONE_ON_ONE_TYPE,
} from '../../../types';
import { Trash2, Plus } from 'react-feather';
import {
  FREQUENCY_OPTIONS,
  MODALITY_LABELS,
  WORKSHOP_PATTERN_OPTIONS,
  SESSION_DURATION_OPTIONS,
  ONE_ON_ONE_DURATION_OPTIONS,
} from '../../../config/cohortConfig';
import type { SessionConfigStepProps, OneOnOneSessionConfigForm } from './types';
import classes from './CreateCohortPage.module.css';
import { useCallback, useEffect, useRef } from 'react';
import { CheckinSettingsFields } from '../components/CheckinSettingsFields';
import { wizardProgramWeeks } from './cohortDateRange';

export function SessionConfigStep({
  form,
  onAddWorkshop,
  onRemoveWorkshop,
  onUpdateWorkshop,
  onCheckinsChange,
}: SessionConfigStepProps) {
  const { selectedModalities, groupCoachingConfig, oneOnOneConfig, workshopConfig } = form.values;

  const hasGroupCoaching = selectedModalities.includes('group_coaching');
  const hasOneOnOne = selectedModalities.includes('one_on_one');
  const hasWorkshop = selectedModalities.includes('workshop');

  // Track previous default duration to detect changes for AC4
  const prevDefaultDuration = useRef(oneOnOneConfig.sessionDurationMinutes);

  // Build per-session configs helper
  const buildSessionConfigs = useCallback(
    (count: number, defaultDuration: number, existing: OneOnOneSessionConfigForm[]): OneOnOneSessionConfigForm[] => {
      const configs: OneOnOneSessionConfigForm[] = [];
      for (let i = 0; i < count; i++) {
        if (i < existing.length) {
          // Keep existing config (AC4: individual overrides persist)
          configs.push({ ...existing[i], sessionNumber: i + 1 });
        } else {
          // New sessions use default (AC5)
          configs.push({
            sessionNumber: i + 1,
            durationMinutes: defaultDuration,
            oneOnOneType: DEFAULT_ONE_ON_ONE_TYPE,
          });
        }
      }
      return configs;
    },
    [],
  );

  // Sync session configs when session count changes (AC1, AC5-6)
  useEffect(() => {
    if (!hasOneOnOne) return;
    const { sessionCount, sessionDurationMinutes, sessionConfigs } = oneOnOneConfig;
    if (sessionCount > 0 && sessionConfigs.length !== sessionCount) {
      const newConfigs = buildSessionConfigs(sessionCount, sessionDurationMinutes, sessionConfigs);
      form.setFieldValue('oneOnOneConfig.sessionConfigs', newConfigs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOneOnOne, oneOnOneConfig.sessionCount]);

  // When top-level duration changes, update sessions that haven't been individually overridden (AC3)
  useEffect(() => {
    if (!hasOneOnOne) return;
    const { sessionDurationMinutes, sessionConfigs } = oneOnOneConfig;
    if (prevDefaultDuration.current !== sessionDurationMinutes && sessionConfigs.length > 0) {
      const updated = sessionConfigs.map((c) => {
        // Only update sessions whose duration matches the old default (AC4: overrides persist)
        if (c.durationMinutes === prevDefaultDuration.current) {
          return { ...c, durationMinutes: sessionDurationMinutes };
        }
        return c;
      });
      form.setFieldValue('oneOnOneConfig.sessionConfigs', updated);
      prevDefaultDuration.current = sessionDurationMinutes;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOneOnOne, oneOnOneConfig.sessionDurationMinutes]);

  // MED-1350: the check-ins sit at the end of the 1:1 section, or at the end
  // of the Group Coaching section when the cohort has no 1:1 coaching. Only
  // the switches show here. The target weeks and their errors are on the
  // Dates step, beside the other session dates.
  const checkinFields = (
    <CheckinSettingsFields
      value={form.values.checkins}
      onChange={onCheckinsChange}
      programWeeks={wizardProgramWeeks(form.values)}
      showWeeks={false}
    />
  );

  const updateSessionConfig = (index: number, updates: Partial<OneOnOneSessionConfigForm>) => {
    const newConfigs = [...oneOnOneConfig.sessionConfigs];
    newConfigs[index] = { ...newConfigs[index], ...updates };
    form.setFieldValue('oneOnOneConfig.sessionConfigs', newConfigs);
  };

  return (
    <Stack gap="lg">
      <Box>
        <Text size="lg" fw={600}>Session Configuration</Text>
        <Text size="sm" c="dimmed" mt="xs">
          Configure session details for each selected modality.
        </Text>
      </Box>

      {/* Group Coaching Configuration */}
      {hasGroupCoaching && (
        <Box className={classes.wizardSection}>
          <Text size="sm" fw={600} mb="md">{MODALITY_LABELS.group_coaching}</Text>

          <Group align="flex-end" gap="xl">
            <Box>
              <Text size="sm" fw={500} mb="xs" style={{ whiteSpace: 'nowrap' }}>Number of Sessions</Text>
              <NumberInput
                value={groupCoachingConfig.sessionCount}
                onChange={(value) => form.setFieldValue('groupCoachingConfig', {
                  ...groupCoachingConfig,
                  sessionCount: typeof value === 'number' ? value : 6,
                })}
                min={1}
                max={24}
                size="sm"
                style={{ width: 100 }}
              />
            </Box>

            <Box>
              <Text size="sm" fw={500} mb="xs" style={{ whiteSpace: 'nowrap' }}>Session Duration</Text>
              <Select
                value={String(groupCoachingConfig.sessionDurationMinutes)}
                onChange={(value) => form.setFieldValue('groupCoachingConfig', {
                  ...groupCoachingConfig,
                  sessionDurationMinutes: value ? parseInt(value, 10) : 90,
                })}
                data={SESSION_DURATION_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                size="sm"
                style={{ width: 110 }}
              />
            </Box>

            <Box flex={1}>
              <Text size="sm" fw={500} mb="xs">Frequency</Text>
              <Box className={classes.segmentedControl}>
                {FREQUENCY_OPTIONS.map((option) => (
                  <UnstyledButton
                    key={option.value}
                    onClick={() => form.setFieldValue('groupCoachingConfig', {
                      ...groupCoachingConfig,
                      frequency: option.value,
                    })}
                    className={classes.optionButton}
                    data-selected={groupCoachingConfig.frequency === option.value || undefined}
                  >
                    {option.label}
                  </UnstyledButton>
                ))}
              </Box>
            </Box>
          </Group>

          {!hasOneOnOne && checkinFields}
        </Box>
      )}

      {/* 1:1 Coaching Configuration */}
      {hasOneOnOne && (
        <Box className={classes.wizardSection}>
          <Text size="sm" fw={600} mb="md">{MODALITY_LABELS.one_on_one}</Text>

          <Group align="flex-end" gap="xl">
            <Box>
              <Text size="sm" fw={500} mb="xs" style={{ whiteSpace: 'nowrap' }}>Number of Sessions</Text>
              <NumberInput
                value={oneOnOneConfig.sessionCount}
                onChange={(value) => form.setFieldValue('oneOnOneConfig', {
                  ...oneOnOneConfig,
                  sessionCount: typeof value === 'number' ? value : 4,
                })}
                min={1}
                max={24}
                size="sm"
                style={{ width: 100 }}
              />
            </Box>

            <Box>
              <Text size="sm" fw={500} mb="xs" style={{ whiteSpace: 'nowrap' }}>Session Duration</Text>
              <Select
                value={String(oneOnOneConfig.sessionDurationMinutes)}
                onChange={(value) => form.setFieldValue('oneOnOneConfig', {
                  ...oneOnOneConfig,
                  sessionDurationMinutes: value ? parseInt(value, 10) : 60,
                })}
                data={ONE_ON_ONE_DURATION_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                size="sm"
                style={{ width: 110 }}
              />
            </Box>

            <Box flex={1}>
              <Text size="sm" fw={500} mb="xs">Frequency</Text>
              <Box className={classes.segmentedControl}>
                {FREQUENCY_OPTIONS.map((option) => (
                  <UnstyledButton
                    key={option.value}
                    onClick={() => form.setFieldValue('oneOnOneConfig', {
                      ...oneOnOneConfig,
                      frequency: option.value,
                    })}
                    className={classes.optionButton}
                    data-selected={oneOnOneConfig.frequency === option.value || undefined}
                  >
                    {option.label}
                  </UnstyledButton>
                ))}
              </Box>
            </Box>
          </Group>

          {/* Per-Session Configuration (AC1-6) */}
          {oneOnOneConfig.sessionConfigs.length > 0 && (
            <Box mt="md">
              <Divider mb="sm" />
              <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="sm" style={{ letterSpacing: '0.3px' }}>
                Session Configuration
              </Text>
              <Stack gap="xs">
                {oneOnOneConfig.sessionConfigs.map((config, index) => (
                  <Group
                    key={config.sessionNumber}
                    gap="md"
                    p="xs"
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #E8E6E3',
                    }}
                  >
                    <Text size="sm" fw={500} style={{ width: 90 }}>
                      Session {config.sessionNumber}
                    </Text>
                    <Select
                      value={String(config.durationMinutes)}
                      onChange={(value) =>
                        updateSessionConfig(index, {
                          durationMinutes: value ? parseInt(value, 10) : oneOnOneConfig.sessionDurationMinutes,
                        })
                      }
                      data={ONE_ON_ONE_DURATION_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                      size="xs"
                      style={{ width: 100 }}
                    />
                    <Select
                      aria-label={`Session ${config.sessionNumber} type`}
                      value={config.oneOnOneType}
                      onChange={(value) =>
                        updateSessionConfig(index, {
                          oneOnOneType:
                            (value as typeof ONE_ON_ONE_TYPES[number]) ?? DEFAULT_ONE_ON_ONE_TYPE,
                        })
                      }
                      data={ONE_ON_ONE_TYPES.map(t => ({
                        value: t,
                        label: ONE_ON_ONE_TYPE_LABELS[t],
                      }))}
                      size="xs"
                      style={{ flex: 1, minWidth: 200 }}
                    />
                  </Group>
                ))}
              </Stack>
            </Box>
          )}

          {checkinFields}
        </Box>
      )}

      {/* Workshop Configuration */}
      {hasWorkshop && (
        <Box className={classes.wizardSection}>
          <Text size="sm" fw={600} mb="md">{MODALITY_LABELS.workshop}</Text>

          {/* Pattern Selection */}
          <Box mb="lg">
            <Text size="sm" fw={500} mb="xs">Workshop Pattern</Text>
            <Box className={classes.segmentedControl} mb="xs">
              {WORKSHOP_PATTERN_OPTIONS.map((option) => (
                <UnstyledButton
                  key={option.value}
                  onClick={() => form.setFieldValue('workshopConfig', {
                    ...workshopConfig,
                    pattern: option.value,
                  })}
                  className={classes.optionButton}
                  data-selected={workshopConfig.pattern === option.value || undefined}
                >
                  {option.label}
                </UnstyledButton>
              ))}
            </Box>
            <Text size="xs" c="dimmed">
              {WORKSHOP_PATTERN_OPTIONS.find(o => o.value === workshopConfig.pattern)?.description}
            </Text>
          </Box>

          {/* Workshop List */}
          <Stack gap="sm">
            {workshopConfig.workshops.map((workshop, index) => (
              <Box
                key={index}
                p="md"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #E8E6E3',
                }}
              >
                <Group wrap="nowrap" align="flex-start" gap="md">
                  {workshopConfig.pattern === 'sequential' && (
                    <Box
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-medleyGreen-1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Text size="sm" fw={600} c="medleyGreen">
                        {index + 1}
                      </Text>
                    </Box>
                  )}

                  <Box flex={1}>
                    <Group gap="sm" mb="xs" align="flex-end">
                      <TextInput
                        placeholder="Workshop title"
                        value={workshop.title}
                        onChange={(e) => onUpdateWorkshop(index, { title: e.currentTarget.value })}
                        size="sm"
                        style={{ flex: 1 }}
                      />
                      <Select
                        value={String(workshop.sessionDurationMinutes)}
                        onChange={(value) => onUpdateWorkshop(index, {
                          sessionDurationMinutes: value ? parseInt(value, 10) : 60,
                        })}
                        data={SESSION_DURATION_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                        size="sm"
                        style={{ width: 110 }}
                        placeholder="Duration"
                      />
                    </Group>

                    <Switch
                      label="Track attendance"
                      size="xs"
                      checked={workshop.attendance_tracking_enabled}
                      onChange={(e) => onUpdateWorkshop(index, {
                        attendance_tracking_enabled: e.currentTarget.checked,
                      })}
                    />
                  </Box>

                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => onRemoveWorkshop(index)}
                  >
                    <Trash2 size={14} />
                  </ActionIcon>
                </Group>
              </Box>
            ))}

            {/* Add Workshop Button */}
            <UnstyledButton
              onClick={onAddWorkshop}
              p="md"
              style={{
                borderRadius: '8px',
                border: '2px dashed #D0CEC9',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: 'var(--mantine-color-gray-6)',
                transition: 'all 0.15s ease',
              }}
            >
              <Plus size={16} />
              <Text size="sm" fw={500}>Add Workshop</Text>
            </UnstyledButton>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
